import React from 'react';
import { Square } from 'lucide-react';
import { renderNote } from './PhaseNotes';

/**
 * McduTutorial - Stage 3 long-form MCDU tutorial renderer (Glass Cockpit redesign).
 *
 * Renders the DIFSRIP tracker (7 tiles, static roadmap - the tutorial is one
 * continuous scroll with no per-tile done/current state in the data) followed
 * by each section in order, with blocks rendered per their `block` type: text,
 * note (NOTE/STEP/CHECK/INSIGHT card, reusing PhaseNotes' renderNote), heading3,
 * table, steps (numbered cards with inline STEP/CHECK labels), and checklist
 * (static tick-style list, used only in the Final Cross-Check section).
 *
 * Props:
 * - tutorial: { difsrip_tracker: [{letter, label}], sections: [{id, heading, blocks}] }
 */
const DifsripTracker = (props) => {
  const tracker = props.tracker;
  if (!tracker || tracker.length === 0) return null;
  return (
    <div className="grid grid-cols-7 gap-1.5 sm:gap-2" data-testid="difsrip-tracker">
      {tracker.map((tile, idx) => (
        <div
          key={idx}
          className="flex flex-col items-center justify-center h-[46px] px-1 rounded-[9px] bg-glass-card border"
          style={{ borderColor: 'var(--gc-border-outline)' }}
          data-testid={`difsrip-tile-${idx}`}
        >
          <span className="font-barlow-condensed text-base font-bold leading-none" style={{ color: 'var(--gc-cyan)' }}>
            {tile.letter}
          </span>
          <span className="font-plex-mono text-[8px] sm:text-[9px] uppercase tracking-tight mt-0.5 text-center leading-tight" style={{ color: 'var(--gc-text-faint)' }}>
            {tile.label}
          </span>
        </div>
      ))}
    </div>
  );
};

const TableBlock = (props) => (
  <div
    className="my-3 overflow-x-auto rounded-gc-card border"
    style={{ background: 'var(--gc-bg)', borderColor: 'var(--gc-border-card)' }}
    data-testid={`mcdu-table-${props.index}`}
  >
    <table className="w-full text-sm">
      <thead>
        <tr>
          {props.columns.map((col, i) => (
            <th
              key={i}
              className="text-left px-3 py-2 font-plex-mono text-[11px] uppercase tracking-wide border-b whitespace-nowrap"
              style={{ color: 'var(--gc-text-muted)', borderColor: 'var(--gc-border-row)' }}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row, ri) => (
          <tr key={ri} className="border-b last:border-0" style={{ borderColor: 'var(--gc-border-row)' }}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                className="px-3 py-2 font-plex-mono text-xs sm:text-sm whitespace-nowrap"
                style={{ color: 'var(--gc-cyan)' }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StepsBlock = (props) => (
  <div className="flex flex-col gap-2.5 my-3" data-testid={`mcdu-steps-${props.index}`}>
    {props.items.map((item, i) => {
      const isCheck = item.label === 'CHECK';
      const labelColor = isCheck ? 'var(--gc-green)' : 'var(--gc-cyan)';
      return (
        <div
          key={i}
          className="flex items-start gap-3 rounded-gc-card border bg-glass-card px-4 py-3"
          style={{ borderColor: 'var(--gc-border-card)' }}
        >
          <span className="shrink-0 font-barlow-condensed font-bold text-xl leading-none mt-0.5" style={{ color: 'var(--gc-cyan)' }}>
            {i + 1}
          </span>
          <div className="flex flex-col gap-1">
            <span
              className="shrink-0 font-plex-mono text-[10px] font-semibold tracking-[0.08em] self-start px-1.5 py-0.5 rounded-gc-badge border"
              style={{ color: labelColor, borderColor: labelColor }}
            >
              {item.label}
            </span>
            <span className="text-[15px] leading-[1.6]" style={{ color: 'var(--gc-text-body)' }}>
              {item.text}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

const ChecklistBlock = (props) => (
  <div className="flex flex-col gap-2.5 my-3" data-testid={`mcdu-checklist-${props.index}`}>
    {props.items.map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <Square className="w-[18px] h-[18px] mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: 'var(--gc-cyan)' }} />
        <span className="text-[15px] leading-[1.6]" style={{ color: 'var(--gc-text-body)' }}>{item}</span>
      </div>
    ))}
  </div>
);

const renderBlock = (block, index) => {
  switch (block.block) {
    case 'text':
      return (
        <p key={index} className="text-[15px] leading-[1.6] my-3" style={{ color: 'var(--gc-text-body)' }} data-testid={`mcdu-text-${index}`}>
          {block.text}
        </p>
      );
    case 'heading3':
      return (
        <h3
          key={index}
          className="font-barlow-condensed font-semibold text-lg mt-5 mb-2"
          style={{ color: 'var(--gc-text-main)' }}
          data-testid={`mcdu-heading3-${index}`}
        >
          {block.text}
        </h3>
      );
    case 'note':
      return renderNote({ type: block.label, text: block.text }, `mcdu-${index}`);
    case 'table':
      return <TableBlock key={index} columns={block.columns} rows={block.rows} index={index} />;
    case 'steps':
      return <StepsBlock key={index} items={block.items} index={index} />;
    case 'checklist':
      return <ChecklistBlock key={index} items={block.items} index={index} />;
    default:
      return null;
  }
};

const McduTutorial = (props) => {
  const tutorial = props.tutorial;
  if (!tutorial) return null;

  return (
    <div className="mt-8" data-testid="mcdu-tutorial">
      <DifsripTracker tracker={tutorial.difsrip_tracker} />

      <div className="mt-6 flex flex-col gap-8">
        {(tutorial.sections || []).map((section) => (
          <section key={section.id} data-testid={`mcdu-section-${section.id}`}>
            <h2
              className="font-barlow-condensed font-bold text-2xl mb-3 pl-3.5"
              style={{ color: 'var(--gc-text-main)', borderLeft: '3px solid var(--gc-cyan)' }}
            >
              {section.heading}
            </h2>
            <div>
              {(section.blocks || []).map((block, bi) => renderBlock(block, bi))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default McduTutorial;
